import { createSlice } from '@reduxjs/toolkit';

import {
  activateHub,
  activateLogin,
  addProfileSidebarItem,
  changeLoginData,
  createProfileBodyArt,
  deactivateHub,
  deleteProfileBodyArt,
  getConnections,
  getFullProfileInfo,
  getProfileInfo,
  getProfileSettings,
  getSettingsProfileInfo,
  getSyncedCalendarProviders,
  removeProfileSidebarItem,
  setProfileSettings,
  updateFeedsCounter,
  updateProfileAppearance,
  updateProfileBodyArt,
  updateProfileContacts,
  updateProfileFullName,
  updateProfileGeneralInfo,
} from './profile.actions';

import type { PayloadAction } from '@reduxjs/toolkit';
import type { AvatarModel } from '@shared/models/avatar.model';
import type { HubsItemsModel } from '@shared/models/hubsItems.model';
import type { MediaType } from '@shared/models/media.model';
import type { TaskItemModel } from '@shared/models/tasks/taskItem.model';
export interface ProfileState {
  isFetching: boolean;
  data: any;
  connections: any;
  hubs_items?: HubsItemsModel;
}

const initialState: ProfileState = {
  isFetching: false,
  data: null,
  connections: null,
  hubs_items: undefined,
};

export const profileSlice = createSlice({
  name: 'profileSlice',
  initialState,
  reducers: {
    setProfileViewDataItem: (state, action: PayloadAction<string>) => {
      if (state.data.view_data) {
        state.data.view_data[action.payload] = true;
      }
    },
    setProfileHubsGarageExpiredAtDate: (state, action: PayloadAction<boolean>) => {
      state.data.hubs.garage.expired_at = action.payload;
    },
    setToggleCalendarProvider: (state, action: PayloadAction<number>) => {
      const provider = state.data.calendar_provider.find((p: any) => p.id === action.payload);
      if (provider) {
        provider.is_sync = !provider.is_sync;
      }
    },

    setProfileEmail: (state, action: PayloadAction<string>) => {
      state.data.email = action.payload;
    },
    setProfileHubsItems: (state, action: PayloadAction<HubsItemsModel>) => {
      state.hubs_items = action.payload;
    },
    setProfilePhone: (state, action: PayloadAction<string>) => {
      state.data.phone = action.payload;
    },
    deleteProfileAvatar: (state) => {
      state.data = { ...state.data, avatar: null };
    },
    setProfileAvatar: (state, action: PayloadAction<AvatarModel>) => {
      state.data = { ...state.data, avatar: action.payload };
    },
    deleteProfileBackground: (state) => {
      state.data = { ...state.data, background: null };
    },
    setProfileBackground: (state, action: PayloadAction<AvatarModel>) => {
      state.data = { ...state.data, background: action.payload };
    },
    setProfileGallery: (state, action: PayloadAction<MediaType[]>) => {
      state.data = { ...state.data, gallery: action.payload };
    },

    setProfileNotificationsCount: (state, action: PayloadAction<number>) => {
      state.data = {
        ...state.data,
        counters: {
          ...state.data.counters,
          count_requests: state.data.counters.count_requests + action.payload,
        },
      };
    },
    reorderSidebarOrganizerItems: (state, action: PayloadAction<any>) => {
      const reorderedItem = state.data?.sidebar?.organizers?.splice(action.payload.droppableIndex, 1);
      state.data.sidebar.organizers.splice(action.payload.destinationIndex, 0, ...reorderedItem);
    },
    reorderSidebarHubsItems: (state, action: PayloadAction<any>) => {
      const reorderedItem = state.data?.sidebar?.hubs?.splice(action.payload.droppableIndex, 1);
      state.data.sidebar.hubs.splice(action.payload.destinationIndex, 0, ...reorderedItem);
    },
    setProfileNotificationsNewsCount: (state, action: PayloadAction<number>) => {
      state.data = {
        ...state.data,
        counters: {
          ...state.data.counters,
          count_notification: state.data.counters.count_notification + action.payload,
          count_news: state.data.counters.count_news + action.payload,
        },
      };
    },
    setUpdatedProviderCalendar: (state, action: PayloadAction<any>) => {
      const updatedCalendar = action.payload;

      state.data.calendar_provider = state.data.calendar_provider.map((provider: any) => {
        return {
          ...provider,
          calendars: provider.calendars.map((calendar: any) =>
            calendar.id === updatedCalendar.id ? updatedCalendar : calendar,
          ),
        };
      });
    },
    setProfileNotificationsAllNewsCount: (state) => {
      state.data = {
        ...state.data,
        counters: {
          ...state.data.counters,
          count_notification: state.data.counters.count_notification - state.data.counters.count_news,
          count_news: 0,
        },
      };
    },
    resetProfileState: () => {
      return {
        ...initialState,
      };
    },
    updateUserNameData: (state, { payload }: PayloadAction<any>) => {
      state.data = {
        ...state.data,
        first_name: payload.first_name,
        last_name: payload.last_name,
        full_name: payload.full_name,
      };
    },
    clearVerification: (state, { payload }: PayloadAction<{ isPhone: boolean }>) => {
      state.data = {
        ...state.data,
        unverified_login: {
          ...state.data.unverified_login,
          ...(payload.isPhone ? { phone: null } : { email: null }),
        },
      };
    },
  },
  extraReducers: ({ addCase }) => {
    addCase(getProfileInfo.fulfilled, (state, { payload }) => {
      state.isFetching = true;
      state.data = {
        ...state.data,
        ...payload,
        sidebar: {
          ...payload.sidebar,
          organizers: payload?.sidebar?.organizers?.filter(
            (item: any) => item.tag !== 'storage' && item.tag !== 'google_sync',
          ),
        },
      };
    });
    addCase(removeProfileSidebarItem.fulfilled, (state, { payload }) => {
      state.data.sidebar.hubs = state?.data?.sidebar?.hubs.filter(
        (hub: { hub_id: number }) => hub.hub_id !== payload,
      );
    });
    addCase(addProfileSidebarItem.fulfilled, (state, { payload }) => {
      state.data.sidebar.hubs = [...state.data.sidebar.hubs, payload];
    });

    addCase(getConnections.fulfilled, (state, { payload }) => {
      state.connections = payload?.map((item: any) => {
        if (item?.contacts?.is_company) {
          return { ...item, full_name: item?.contacts?.company };
        }
        return item;
      });
    });
    addCase(getSettingsProfileInfo.fulfilled, (state, { payload }) => {
      state.data = { ...state.data, ...payload };
    });
    addCase(getSyncedCalendarProviders.fulfilled, (state, { payload }) => {
      state.data = { ...state.data, calendar_provider: payload };
    });
    addCase(updateProfileFullName.fulfilled, (state, { payload }) => {
      state.data = { ...state.data, ...payload };
    });
    addCase(getFullProfileInfo.fulfilled, (state, { payload }) => {
      state.data = { ...state.data, ...payload };
    });
    addCase(updateProfileGeneralInfo.fulfilled, (state, { payload }) => {
      state.data = { ...state.data, ...payload };
    });
    addCase(updateProfileAppearance.fulfilled, (state, { payload }) => {
      state.data = { ...state.data, appearance: payload };
    });
    addCase(updateProfileContacts.fulfilled, (state, { payload }) => {
      state.data = { ...state.data, contacts: payload };
    });
    addCase(changeLoginData.fulfilled, (state, { payload }) => {
      state.data = { ...state.data, ...payload };
    });
    addCase(activateLogin.fulfilled, (state, { payload }) => {
      if (payload.isPhone) {
        state.data = {
          ...state.data,
          phone: payload.login,
          unverified_login: { ...state.data.unverified_login, phone: null },
        };
      } else {
        state.data = {
          ...state.data,
          email: payload.login,
          unverified_login: { ...state.data.unverified_login, email: null },
        };
      }
    });
    addCase(createProfileBodyArt.fulfilled, (state, { payload }) => {
      state.data = {
        ...state.data,
        bodyArts: [...state.data.bodyArts, payload],
      };
    });
    addCase(updateProfileBodyArt.fulfilled, (state, { payload }) => {
      state.data = {
        ...state.data,
        bodyArts: state.data.bodyArts.map((item: TaskItemModel) => {
          if (item.id === payload.id) {
            return payload;
          }
          return item;
        }),
      };
    });

    addCase(deleteProfileBodyArt.fulfilled, (state, { payload }) => {
      state.data = {
        ...state.data,
        bodyArts: state.data.bodyArts.filter((item: any) => item.id !== payload),
      };
    });

    addCase(activateHub.fulfilled, (state, { payload }) => {
      const hubName = payload.name?.toLowerCase();
      state.data.hubs = { ...state.data.hubs, [hubName]: payload };
    });
    addCase(deactivateHub.fulfilled, (state, { payload }) => {
      const hubName = payload.name?.toLowerCase();
      state.data.hubs = { ...state.data.hubs, [hubName]: payload };
    });
    addCase(updateFeedsCounter.fulfilled, (state, { payload }) => {
      state.data.counters = { ...state.data.counters, ...payload };
    });
    addCase(getProfileSettings.fulfilled, (state, { payload }) => {
      state.data = { ...state?.data, profileSettings: payload };
    });
    addCase(setProfileSettings.fulfilled, (state, { payload }) => {
      state.data = { ...state?.data, profileSettings: { ...state?.data?.profileSettings, ...payload } };
    });
  },
});

export const {
  resetProfileState,
  setProfileViewDataItem,
  setProfileHubsGarageExpiredAtDate,
  deleteProfileAvatar,
  setProfileAvatar,
  deleteProfileBackground,
  setProfileBackground,
  setProfileGallery,
  setToggleCalendarProvider,
  setProfileNotificationsCount,
  setProfileNotificationsAllNewsCount,
  setProfileHubsItems,
  setProfileNotificationsNewsCount,
  reorderSidebarOrganizerItems,
  reorderSidebarHubsItems,
  updateUserNameData,
  setUpdatedProviderCalendar,
  clearVerification,
} = profileSlice.actions;
export default profileSlice.reducer;
